import React from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import { jss, ThemeProvider, withTheme } from 'react-jss';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { get } from '@bingads-webui-universal/primitive-utilities';
import { ITheme } from './types';

const emptyTheme = {};

const styleSheet = jss.createStyleSheet<any>({}).attach();

const getClassName = (theme: ITheme, fieldName: string): string | undefined => {
  const ruleName = fieldName.split('.').pop();

  const elementStyles = get(theme, fieldName);

  let rule = styleSheet.getRule(ruleName);

  if (_.isEmpty(rule) && !_.isEmpty(elementStyles)) {
    // Ensure elementStyles is not undefined and is an object
    rule = styleSheet.addRule(ruleName as any, elementStyles as object);
  }


  return get(rule, `options.classes.${ruleName}`);
};

export function withStyleOverride<C extends React.ComponentType<any>>(Component: C, fieldNames: string[] = []) {
  type StyledComponentProps = React.ComponentProps<C> & {
    theme?: ITheme;
    forwardRef?: React.Ref<React.ComponentRef<C>>;
  };
  const ComponentWithRef = Component as React.ComponentType<
    React.ComponentProps<C> & React.RefAttributes<React.ComponentRef<C>>
  >;

  const StyledComponent: React.FC<StyledComponentProps> = ({
    theme = emptyTheme,
    className = '',
    forwardRef = null,
    ...props
  }) => {
    const themeClasses = _.map(fieldNames, _.partial(getClassName, theme));
    const nameProp = classNames(themeClasses, className);
    const componentProps = {
      ...props,
      className: _.isEmpty(nameProp) ? null : nameProp,
      ref: forwardRef,
    } as React.ComponentProps<typeof ComponentWithRef>;

    return (
      <ComponentWithRef {...componentProps} />
    );
  };

  const WrappedComponent: React.ComponentType<Omit<StyledComponentProps, 'theme'>> = withTheme(StyledComponent as any) as any;

  const ThemedComponent = React.forwardRef<React.ComponentRef<C>, React.PropsWithoutRef<React.ComponentProps<C>>>((props, ref) => {
    const wrappedProps = {
      ...props,
      forwardRef: ref,
    } as Omit<StyledComponentProps, 'theme'>;

    return (
      <ThemeProvider theme={emptyTheme}>
        <WrappedComponent {...wrappedProps} />
      </ThemeProvider>
    );
  });

  return hoistNonReactStatics(ThemedComponent, Component);
}
